import { useEffect, useMemo } from 'react';
import css from './TypedText.module.css';
import { useTypewriter } from './use-typewriter';

type Segment = { text: string; mark: boolean };

function parseMarks(source: string): Segment[] {
  return source
    .split('**')
    .map((text, index) => ({ text, mark: index % 2 === 1 }))
    .filter((segment) => segment.text);
}

export function TypedText({
  text,
  enabled = true,
  reserve = true,
  onDone,
}: {
  text: string;
  enabled?: boolean;
  reserve?: boolean;
  onDone?: () => void;
}) {
  const segments = useMemo(() => parseMarks(text), [text]);
  const plain = useMemo(
    () => segments.map((segment) => segment.text).join(''),
    [segments],
  );
  const { count, isTyping, isDone } = useTypewriter(plain, enabled);

  useEffect(() => {
    if (isDone) onDone?.();
  }, [isDone, onDone]);

  let offset = 0;
  const typed = segments.map((segment, index) => {
    const shown = segment.text.slice(0, Math.max(0, count - offset));
    offset += segment.text.length;
    if (!shown) return null;
    return segment.mark ? (
      <mark key={index} className={css.mark}>
        {shown}
      </mark>
    ) : (
      shown
    );
  });

  return (
    <>
      {typed}
      {isTyping && <span className={css.caret} aria-hidden />}
      {reserve && <span className={css.ghost}>{plain.slice(count)}</span>}
    </>
  );
}
